// A town and its country: the only location Guildite keeps from "Use my
// current location", town suggestions and place tags on posts and listings.
// Never a point or an address.
export type Place = { town: string; country: string };

export const TOWN_MAX_LENGTH = 60;

// The money each country's prices are usually in, for the "prices are in …"
// hint when someone browses from a country whose money differs.
const COUNTRY_CURRENCY: Record<string, string> = {
  GH: 'GHS', NG: 'NGN', TG: 'XOF', CI: 'XOF', BJ: 'XOF', BF: 'XOF', SN: 'XOF', KE: 'KES', ZA: 'ZAR',
  US: 'USD', CA: 'CAD', GB: 'GBP', IE: 'EUR', DE: 'EUR', FR: 'EUR', NL: 'EUR', IT: 'EUR', ES: 'EUR', AE: 'AED',
};

const CURRENCY_NAMES: Record<string, string> = { GHS: 'Ghana cedis', NGN: 'naira', XOF: 'CFA francs', KES: 'Kenyan shillings', USD: 'US dollars' };

function cleanTown(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, TOWN_MAX_LENGTH);
}

// A town and a two-letter country code, or null. The town is a place name:
// letters, spaces and simple punctuation, never a number or an address.
export function parsePlace(raw: unknown): Place | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  const town = cleanTown(value.town);
  const country = String(value.country ?? '').trim().toUpperCase();
  if (town.length < 2 || !/^[\p{L}][\p{L} .'’()-]*$/u.test(town)) return null;
  if (!/^[A-Z]{2}$/.test(country)) return null;
  return { town, country };
}

function fold(value: string): string {
  return value.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

// The value stored next to a tag so "more from this town" is one equality
// query: "gh:kumasi", "ca:toronto".
export function placeKey(place: Place): string {
  return `${place.country.toLowerCase()}:${fold(place.town).replace(/ /g, '-')}`;
}

export function placeFromKey(key: string): Place | null {
  const match = /^([a-z]{2}):([\p{L}\p{N}-]{2,60})$/u.exec(key);
  if (!match) return null;
  const town = match[2]!.split('-').filter(Boolean).map(word => word[0]!.toUpperCase() + word.slice(1)).join(' ');
  return parsePlace({ town, country: match[1]!.toUpperCase() });
}

export function samePlace(a: Place | null | undefined, b: Place | null | undefined): boolean {
  return Boolean(a && b && placeKey(a) === placeKey(b));
}

// True when a free-text area ("Adum, Kumasi") names the town. Used to rank
// older posts, shops and listings that have an area name but no place tag.
export function areaInTown(area: string | null | undefined, town: string): boolean {
  const wanted = fold(town);
  if (!wanted || !area) return false;
  return ` ${fold(area)} `.includes(` ${wanted} `);
}

// Items in the town first, keeping the original order inside each group.
export function localFirst<T>(items: T[], isLocal: (item: T) => boolean): T[] {
  return [...items.filter(isLocal), ...items.filter(item => !isLocal(item))];
}

export function countryCurrency(country: string): string | null {
  return COUNTRY_CURRENCY[country.toUpperCase()] ?? null;
}

// "Prices are in Ghana cedis (GHS)." when the viewer's country uses other
// money, or null when it is the same (or unknown).
export function currencyHint(viewerCountry: string | null | undefined, priceCurrency: string): string | null {
  if (!viewerCountry) return null;
  const local = countryCurrency(viewerCountry);
  if (!local || local === priceCurrency) return null;
  const name = CURRENCY_NAMES[priceCurrency];
  return `Prices are in ${name ? `${name} (${priceCurrency})` : priceCurrency}.`;
}
